'use client';

import { useRef } from 'react';

import { motion, useInView, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';

import { AdsCreationAnimation } from './animations/ads-creation-animation';
import { AdsResearchAnimation } from './animations/ads-research-animation';
import { buildChatCopy } from './animations/chat-copy';
import { ChatQualifyAnimation } from './animations/chat-qualify-animation';
import { DeliveryAnimation } from './animations/delivery-animation';

/**
 * The engine — the page centerpiece. The four operational domains shown as one
 * connected funnel: Research -> Create -> Qualify -> Deliver. Each row pairs a
 * scripted, in-view, reduced-motion-safe demo with plain-spoken copy. Replaces
 * the old three-engine split + five-step pipeline. Section order follows the
 * selenos-marketing-page skill (this sits after the problem framing).
 */
/** Teal "data flow" segments gliding down the spine — ties the four rows into
 * one funnel. Transform/opacity only; hidden under reduced motion. */
function SpineFlow() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  // Unmount the loop offscreen so it never animates outside the viewport.
  const inView = useInView(ref, { amount: 0.1 });
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="absolute top-2 bottom-2 left-[15px] hidden w-px overflow-visible md:block"
    >
      {!reduce &&
        inView &&
        [0, 1].map((i) => (
          // Full-height wrapper: translateY percentages resolve against the
          // spine height, so the segment sweeps it via transform only.
          <motion.span
            key={i}
            className="absolute inset-0 block"
            initial={{ y: '-8%', opacity: 0 }}
            animate={{ y: ['-8%', '108%'], opacity: [0, 0.75, 0.75, 0] }}
            transition={{
              duration: 4.6,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 2.3,
            }}
          >
            <span
              className="absolute top-0 left-1/2 block h-10 w-[3px] -translate-x-1/2 rounded-full"
              style={{
                background:
                  'linear-gradient(to bottom, transparent, var(--sel-accent), transparent)',
              }}
            />
          </motion.span>
        ))}
    </div>
  );
}

export function FunnelShowcase() {
  const t = useTranslations('selenos');

  const rows = [
    {
      n: '01',
      engine: t('domain1Engine'),
      name: t('domain1Name'),
      tag: t('domain1Tag'),
      points: [t('domain1Point1'), t('domain1Point2')],
      demo: (
        <AdsResearchAnimation
          copy={{
            frameLabel: t('animResearchFrame'),
            top: t('animResearchTop'),
            hookLabel: t('animResearchHookLabel'),
            hook: t('animResearchHook'),
          }}
        />
      ),
    },
    {
      n: '02',
      engine: t('domain2Engine'),
      name: t('domain2Name'),
      tag: t('domain2Tag'),
      points: [t('domain2Point1'), t('domain2Point2')],
      demo: (
        <AdsCreationAnimation
          copy={{
            frameLabel: t('animCreateFrame'),
            headline: t('animCreateHeadline'),
            cta: t('animCreateCta'),
            live: t('animCreateLive'),
            cplLabel: t('animCreateCplLabel'),
            cplValue: t('animCreateCplValue'),
          }}
        />
      ),
    },
    {
      n: '03',
      engine: t('domain3Engine'),
      name: t('domain3Name'),
      tag: t('domain3Tag'),
      points: [t('domain3Point1'), t('domain3Point2')],
      demo: (
        <ChatQualifyAnimation
          copy={buildChatCopy(t)}
          className="mx-auto max-w-[250px]"
        />
      ),
    },
    {
      n: '04',
      engine: t('domain4Engine'),
      name: t('domain4Name'),
      tag: t('domain4Tag'),
      points: [t('domain4Point1'), t('domain4Point2')],
      demo: (
        <DeliveryAnimation
          copy={{
            frameLabel: t('animDeliverFrame'),
            leadName: t('animDeliverLead'),
            qualified: t('animDeliverQualified'),
            destinations: [
              t('animDeliverD1'),
              t('animDeliverD2'),
              t('animDeliverD3'),
              t('animDeliverD4'),
            ],
            delivered: t('animDeliverDelivered'),
          }}
        />
      ),
    },
  ];

  return (
    <section id="how" className="scroll-mt-24 py-24 sm:py-32">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        <p className="sel-mono text-xs tracking-[0.18em] text-[var(--sel-muted)] uppercase">
          {t('showcaseEyebrow')}
        </p>
        <h2 className="mt-4 max-w-[36rem] text-[clamp(2rem,4vw,3.25rem)] leading-[1.08] font-semibold tracking-[-0.02em] text-[var(--sel-ink)]">
          {t('showcaseHeading')}
        </h2>
        <p className="mt-5 max-w-[40rem] text-[1.0625rem] leading-relaxed text-[var(--sel-muted)]">
          {t('showcaseSub')}
        </p>

        {/* The connected funnel. A teal spine runs down the row index column. */}
        <div className="relative mt-16">
          <div
            aria-hidden="true"
            className="absolute top-2 bottom-2 left-[15px] hidden w-px bg-gradient-to-b from-[var(--sel-accent)]/40 via-[var(--sel-line)] to-[var(--sel-accent)]/40 md:block"
          />
          <SpineFlow />
          <div className="flex flex-col gap-16 md:gap-24">
            {rows.map((row, i) => (
              <div
                key={row.n}
                className="grid items-center gap-8 md:grid-cols-2 md:gap-14"
              >
                <div className={i % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="flex items-center gap-4">
                    <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--sel-accent)] bg-[var(--sel-bg)]">
                      <span className="sel-mono text-xs font-semibold text-[var(--sel-accent)]">
                        {row.n}
                      </span>
                    </span>
                    <span className="sel-mono text-xs tracking-[0.14em] text-[var(--sel-faint)] uppercase">
                      {row.engine}
                    </span>
                  </div>
                  {/* md:pl-12 = badge width (32px) + gap (16px), so the copy
                      clears the spine and aligns with the engine label. */}
                  <h3 className="mt-5 text-[clamp(1.5rem,2.6vw,2.125rem)] leading-tight font-semibold tracking-[-0.01em] text-[var(--sel-ink)] md:pl-12">
                    {row.name}
                  </h3>
                  <p className="mt-3 text-[1.0625rem] leading-relaxed text-[var(--sel-muted)] md:pl-12">
                    {row.tag}
                  </p>
                  <ul className="mt-5 flex flex-col gap-3 md:pl-12">
                    {row.points.map((point) => (
                      <li
                        key={point}
                        className="flex items-baseline gap-3 text-[0.9375rem] leading-relaxed text-[var(--sel-muted)]"
                      >
                        <span
                          aria-hidden="true"
                          className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--sel-accent)]"
                        />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={i % 2 === 1 ? 'md:order-1' : ''}>
                  {row.demo}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
